<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import type { GameErrorNotice } from '../core/gameError'
import { uiText } from '../core/ui'

const notice = ref<GameErrorNotice | null>(null)

function onError(event: Event): void {
  const custom = event as CustomEvent<GameErrorNotice>
  notice.value = custom.detail
}

function close(): void {
  notice.value = null
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && notice.value !== null) close()
}

onMounted(() => {
  window.addEventListener('ci:command-error', onError)
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('ci:command-error', onError)
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Transition name="game-error-pop">
    <aside
      v-if="notice"
      class="game-error-toast"
      role="alertdialog"
      aria-live="assertive"
      aria-atomic="true"
    >
      <header class="game-error-toast__header">
        <strong>{{ notice.title }}</strong>
        <button
          type="button"
          class="game-error-toast__close"
          :aria-label="uiText('Fehlermeldung schließen', 'Close error message')"
          @click="close"
        >
          ×
        </button>
      </header>
      <p class="game-error-toast__message">{{ notice.message }}</p>
      <ul v-if="notice.details.length > 0" class="game-error-toast__details">
        <li v-for="detail in notice.details" :key="detail">{{ detail }}</li>
      </ul>
      <details class="game-error-toast__technical">
        <summary>{{ uiText('Technische Details', 'Technical details') }}</summary>
        <code>{{ notice.code }}</code>
        <span v-if="notice.correlationId">
          {{ uiText('Referenz', 'Reference') }}: {{ notice.correlationId }}
        </span>
      </details>
    </aside>
  </Transition>
</template>
